import { useEffect, useRef, useCallback } from 'react';

export interface PixelInfo {
  facebookPixelId?: string | null;
  kakaoPixelId?: string | null;
  googleAdsPixelId?: string | null;
  googleAdsEventSnippet?: string | null;
  daangnPixelId?: string | null;
  tiktokPixelId?: string | null;
}

// 스크립트 로드 상태 추적
const loadedScripts: Record<string, boolean> = {};

const loadScript = (id: string, src: string, onLoad?: () => void): void => {
  if (loadedScripts[id] || document.getElementById(id)) {
    onLoad?.();
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  script.onload = () => {
    loadedScripts[id] = true;
    onLoad?.();
  };
  document.head.appendChild(script);
};

const loadInlineScript = (id: string, content: string): void => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.innerHTML = content;
  document.head.appendChild(script);
};

export const usePixel = (pixelInfo: PixelInfo | null) => {
  const initializedRef = useRef(false);
  const pixelInfoRef = useRef<PixelInfo | null>(null);

  // PageView 이벤트 (페이지 로드 시)
  useEffect(() => {
    if (!pixelInfo || initializedRef.current) {
      return;
    }

    // 하나라도 픽셀이 있으면 초기화
    const hasAnyPixel =
      pixelInfo.facebookPixelId ||
      pixelInfo.kakaoPixelId ||
      pixelInfo.googleAdsPixelId ||
      pixelInfo.daangnPixelId ||
      pixelInfo.tiktokPixelId;

    if (!hasAnyPixel) {
      return;
    }

    initializedRef.current = true;
    pixelInfoRef.current = pixelInfo;

    // Facebook Pixel
    if (pixelInfo.facebookPixelId) {
      if ((window as any).fbq) {
        // 이미 SDK가 로드된 경우 init만 추가
        (window as any).fbq('init', pixelInfo.facebookPixelId);
        (window as any).fbq('track', 'PageView');
      } else {
        // SDK 로드 + init
        loadInlineScript(
          'fb-pixel-init',
          `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelInfo.facebookPixelId}');
          fbq('track', 'PageView');
          `
        );
      }
    }

    // Kakao Pixel
    if (pixelInfo.kakaoPixelId) {
      if ((window as any).kakaoPixel) {
        // 이미 SDK가 로드된 경우
        (window as any).kakaoPixel(pixelInfo.kakaoPixelId).pageView();
      } else {
        loadScript('kakao-pixel-sdk', '//t1.daumcdn.net/kas/static/kp.js', () => {
          if ((window as any).kakaoPixel) {
            (window as any).kakaoPixel(pixelInfo.kakaoPixelId).pageView();
          }
        });
      }
    }

    // Google Ads
    if (pixelInfo.googleAdsPixelId) {
      if ((window as any).gtag) {
        // 이미 gtag가 로드된 경우 config만 추가
        (window as any).gtag('config', pixelInfo.googleAdsPixelId);
      } else {
        loadScript(
          'gtag-script',
          `https://www.googletagmanager.com/gtag/js?id=${pixelInfo.googleAdsPixelId}`,
          () => {
            loadInlineScript(
              'gtag-init',
              `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${pixelInfo.googleAdsPixelId}');
              `
            );
          }
        );
      }
    }

    // Daangn (당근) Pixel
    if (pixelInfo.daangnPixelId) {
      if ((window as any).karrotPixel) {
        // 이미 SDK가 로드된 경우
        (window as any).karrotPixel.init(pixelInfo.daangnPixelId);
        (window as any).karrotPixel.track('ViewPage');
      } else {
        loadScript(
          'daangn-pixel-sdk',
          'https://karrot-pixel.business.daangn.com/0.2/karrot-pixel.umd.js',
          () => {
            if ((window as any).karrotPixel) {
              (window as any).karrotPixel.init(pixelInfo.daangnPixelId);
              (window as any).karrotPixel.track('ViewPage');
            }
          }
        );
      }
    }

    // TikTok Pixel
    if (pixelInfo.tiktokPixelId) {
      if ((window as any).ttq) {
        // 이미 SDK가 로드된 경우 load만 추가
        (window as any).ttq.load(pixelInfo.tiktokPixelId);
        (window as any).ttq.page();
        (window as any).ttq.track('ViewContent');
      } else {
        loadInlineScript(
          'tiktok-pixel-init',
          `
          !function (w, d, t) {
            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
            var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
            ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
            ttq.load('${pixelInfo.tiktokPixelId}');
            ttq.page();
            ttq.track('ViewContent');
          }(window, document, 'ttq');
          `
        );
      }
    }
  }, [pixelInfo]);

  // Conversion 이벤트 (폼 제출 성공 시 호출)
  const trackConversion = useCallback(() => {
    const info = pixelInfoRef.current;
    if (!info) {
      return;
    }

    // Facebook - CompleteRegistration
    if (info.facebookPixelId && (window as any).fbq) {
      (window as any).fbq('track', 'CompleteRegistration');
    }

    // Kakao - signUp
    if (info.kakaoPixelId && (window as any).kakaoPixel) {
      (window as any).kakaoPixel(info.kakaoPixelId).signUp();
    }

    // Google Ads - conversion
    if (info.googleAdsPixelId && info.googleAdsEventSnippet && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        send_to: `${info.googleAdsPixelId}/${info.googleAdsEventSnippet}`,
      });
    }

    // Daangn - SubmitApplication
    if (info.daangnPixelId && (window as any).karrotPixel) {
      (window as any).karrotPixel.track('SubmitApplication');
    }

    // TikTok - SubmitForm
    if (info.tiktokPixelId && (window as any).ttq) {
      (window as any).ttq.track('SubmitForm');
    }
  }, []);

  return { trackConversion };
};